import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MessageSquare, Star } from 'lucide-react';

const BACKEND_URL = '';

export function AdminFeedbackTab() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [feedbackModuleFilter, setFeedbackModuleFilter] = useState('all');

  useEffect(() => { loadFeedback(); }, []);

  const loadFeedback = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/feedback`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setFeedbacks(Array.isArray(data) ? data : []); }
    } catch {}
  };

  const filtered = feedbacks.filter(fb => {
    const matchesSearch = !feedbackSearch ||
      fb.user_name?.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
      fb.comment?.toLowerCase().includes(feedbackSearch.toLowerCase());
    const matchesModule = feedbackModuleFilter === 'all' || fb.module_label === feedbackModuleFilter;
    return matchesSearch && matchesModule;
  });

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">User Feedback</h3>
        <p className="text-sm text-slate-600">Review all feedback submitted by community members.</p>
      </div>

      {feedbacks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-brand-navy">{feedbacks.length}</p>
            <p className="text-xs text-slate-500">Total Responses</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-500">
              {(feedbacks.reduce((sum, f) => sum + (f.rating || 0), 0) / feedbacks.length).toFixed(1)}
            </p>
            <p className="text-xs text-slate-500">Avg Rating</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-brand-sage">{new Set(feedbacks.map(f => f.module_label)).size}</p>
            <p className="text-xs text-slate-500">Modules Rated</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-brand-blue">{new Set(feedbacks.map(f => f.user_id)).size}</p>
            <p className="text-xs text-slate-500">Unique Users</p>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="feedback-search-input" placeholder="Search by user name or comment..."
            value={feedbackSearch} onChange={(e) => setFeedbackSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={feedbackModuleFilter} onValueChange={setFeedbackModuleFilter}>
          <SelectTrigger data-testid="feedback-module-filter" className="w-52"><SelectValue placeholder="Filter by module" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {[...new Set(feedbacks.map(f => f.module_label))].filter(Boolean).sort().map(mod => (
              <SelectItem key={mod} value={mod}>{mod}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {feedbacks.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No feedback submitted yet</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((fb, i) => (
            <Card key={fb.feedback_id || i} data-testid={`admin-feedback-${fb.feedback_id || i}`} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="px-2.5 py-0.5 bg-brand-navy/10 text-brand-navy text-xs font-medium rounded-full">{fb.module_label}</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(fb.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  {fb.comment && <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{fb.comment}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-brand-navy to-brand-sage flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {fb.user_name?.charAt(0) || '?'}
                    </div>
                    <span className="text-xs font-medium text-slate-700">{fb.user_name}</span>
                    <span className="text-xs text-slate-400">{fb.user_email}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
